import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type SearchKind = "all" | "public" | "private" | "nasdaq" | "vip" | "hr" | "marketing";

export interface SearchHistoryFilters {
  area_of_interest?: string[];
  total_donation?: number;
  submission_date?: string[];
  essence?: string[];
}

export interface SearchHistoryEntry {
  id: string;
  query: string;
  kind: SearchKind;
  filters?: SearchHistoryFilters;
  resultCount: number;
  timestamp: number;
  count: number;
}

export interface PageVisitEntry {
  id: string;
  type: "company" | "vip";
  name: string;
  companyType?: string;
  logo?: string;
  lastVisited: number;
  visitCount: number;
}

interface SearchHistoryState {
  searches: SearchHistoryEntry[];
  visits: PageVisitEntry[];
  hydrated: boolean;
}

const MAX_SEARCHES = 30;
const MAX_VISITS = 50;

function makeSearchId(query: string, kind: string, filters?: SearchHistoryFilters): string {
  const filterStr = filters && Object.keys(filters).length > 0
    ? JSON.stringify(filters, Object.keys(filters).sort())
    : "";
  return `${query.toLowerCase().trim()}|${kind}|${filterStr}`;
}

function filtersFingerprint(filters?: SearchHistoryFilters): string | undefined {
  if (!filters || Object.keys(filters).length === 0) return undefined;
  return JSON.stringify(filters, Object.keys(filters).sort());
}

const initialState: SearchHistoryState = {
  searches: [],
  visits: [],
  hydrated: false,
};

export const searchHistorySlice = createSlice({
  name: "searchHistory",
  initialState,
  reducers: {
    hydrateSearches: (state, action: PayloadAction<SearchHistoryEntry[]>) => {
      const serverMap = new Map(action.payload.map((s) => [s.id, s]));
      for (const local of state.searches) {
        if (!serverMap.has(local.id)) serverMap.set(local.id, local);
      }
      state.searches = [...serverMap.values()]
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, MAX_SEARCHES);
      state.hydrated = true;
    },

    hydrateVisits: (state, action: PayloadAction<PageVisitEntry[]>) => {
      const serverMap = new Map(action.payload.map((v) => [`${v.id}|${v.type}`, v]));
      for (const local of state.visits) {
        const key = `${local.id}|${local.type}`;
        if (!serverMap.has(key)) serverMap.set(key, local);
      }
      state.visits = [...serverMap.values()]
        .sort((a, b) => b.lastVisited - a.lastVisited)
        .slice(0, MAX_VISITS);
    },

    optimisticRecordSearch: (
      state,
      action: PayloadAction<{
        query: string;
        kind: SearchKind;
        filters?: SearchHistoryFilters;
        resultCount: number;
      }>
    ) => {
      const { query, kind, filters, resultCount } = action.payload;
      if (resultCount <= 0) return;
      state.hydrated = true;

      const id = makeSearchId(query, kind, filters);
      const existing = state.searches.find((s) => s.id === id);

      if (existing) {
        existing.count += 1;
        existing.timestamp = Date.now();
        existing.resultCount = resultCount;
      } else {
        state.searches.unshift({
          id,
          query: query.trim(),
          kind,
          filters: filters && Object.keys(filters).length > 0 ? filters : undefined,
          resultCount,
          timestamp: Date.now(),
          count: 1,
        });
      }

      state.searches.sort((a, b) => b.timestamp - a.timestamp);
      if (state.searches.length > MAX_SEARCHES) {
        state.searches = state.searches.slice(0, MAX_SEARCHES);
      }
    },

    optimisticRemoveSearch: (state, action: PayloadAction<string>) => {
      state.searches = state.searches.filter((s) => s.id !== action.payload);
    },

    optimisticClearSearches: (state) => {
      state.searches = [];
    },

    optimisticRecordVisit: (
      state,
      action: PayloadAction<{
        id: string;
        type: "company" | "vip";
        name: string;
        companyType?: string;
        logo?: string;
      }>
    ) => {
      const { id, type, name, companyType, logo } = action.payload;
      const existing = state.visits.find((v) => v.id === id && v.type === type);

      if (existing) {
        existing.visitCount += 1;
        existing.lastVisited = Date.now();
        if (logo) existing.logo = logo;
      } else {
        state.visits.unshift({
          id,
          type,
          name,
          companyType,
          logo,
          lastVisited: Date.now(),
          visitCount: 1,
        });
      }

      state.visits.sort((a, b) => b.lastVisited - a.lastVisited);
      if (state.visits.length > MAX_VISITS) {
        state.visits = state.visits.slice(0, MAX_VISITS);
      }
    },

    optimisticRemoveVisit: (state, action: PayloadAction<{ id: string; type: string }>) => {
      state.visits = state.visits.filter(
        (v) => !(v.id === action.payload.id && v.type === action.payload.type)
      );
    },

    optimisticClearVisits: (state) => {
      state.visits = [];
    },
  },
});

export const {
  hydrateSearches,
  hydrateVisits,
  optimisticRecordSearch,
  optimisticRemoveSearch,
  optimisticClearSearches,
  optimisticRecordVisit,
  optimisticRemoveVisit,
  optimisticClearVisits,
} = searchHistorySlice.actions;

export { makeSearchId, filtersFingerprint };
